import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "A dirty Webpack trick that reduced our gzipped bundle  size by 55KB",
  "description": "",
  "published": "2017-06-15T00:00:00.000Z",
  "redirect_from": ["/blog/dirty-hack-took-30percent-off-webpack-size/swizec/7657"],
  "categories": "Front End, Technical, Webpack",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "74Twn8krQS4",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`At my day job, we're using Webpack 2 to make our JavaScript as small as possible.`}</p>
    <p>{`Bundle splitting ensures web apps get only the code they need, individual vendor files ensure caching stays as stable as possible, dead code elimination and tree shaking make our files small. Everything is minified and gzipped and cached forever. Fingerprinting ensures the code you download is never stale.`}</p>
    <p>{`Despite all that, hitting our corp site, you're downloading 489KB of JavaScript. That's one of our most optimized apps. The apps that people use to work with our product are worse. Some go up to 1MB of gzipped JavaScript. 😅`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/big-files-screenshot.png",
        "alt": null
      }}></img></p>
    <p>{`If you look at the `}<inlineCode parentName="p">{`BundleAnalyzerPlugin`}</inlineCode>{` output of our Webpack build, you'll see why. The vendors file, all our dependencies, is 238KB of that footprint.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/webpack-chart.png",
        "alt": null
      }}></img></p>
    <p><a parentName="p" {...{
        "href": "https://lodash.com/"
      }}>{`Lodash`}</a>{` and `}<a parentName="p" {...{
        "href": "https://momentjs.com/"
      }}>{`Moment`}</a>{` locales and jQuery everywhere! Most of the code we're shipping is made up of libraries which we use maybe 20% of.`}</p>
    <p>{`We use 32 functions out of all of Lodash for example. I counted. We don't even use Moment locales!`}</p>
    <h2 {...{
      "id": "heres-how-i-optimized-that"
    }}>{`Here's how I optimized that`}</h2>
    <p>{`Here's how I optimized that to take 55KB off our gzipped vendors file. At least for the corp site that is. Results in other apps vary :)`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/better-files-screenshot.png",
        "alt": null
      }}></img></p>
    <p>{`See, corp site vendors file is down to 183KB. Overall, JavaScript footprint is down by 53KB. Not bad for gzipped minified code.`}</p>
    <p>{`Let's look at the unminified `}<inlineCode parentName="p">{`BundleAnalyzerPlugin`}</inlineCode>{` output for our corp site.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/corp-screen-1.png",
        "alt": null
      }}></img></p>
    <p>{`2.1 megs of unminified ungzipped code 😅`}</p>
    <p>{`The first thing I did was to get rid of Moment's locales following a suggestion in `}<a parentName="p" {...{
        "href": "https://github.com/moment/moment/issues/2373"
      }}>{`this issue`}</a>{`. Moment is unfortunately not modularizable. They've been working on it for 2 years, so the best you can do is to use the `}<inlineCode parentName="p">{`webpack.IgnorePlugin`}</inlineCode>{` to remove them from your build.`}</p>
    <p>{`Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// webpack.config.js

// ...
plugins: [
  // ..
  new webpack.IgnorePlugin(/^\\.\\/locale$/, /moment$/),
];
`}</code></pre>
    <p>{`I'm not sure `}<em parentName="p">{`how`}</em>{` that works, but it removes most of what makes Moment big. We weren't using locales anyway; all our string formatting used explicit format strings.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/corp-screen-2.png",
        "alt": null
      }}></img></p>
    <p>{`That was easy.`}</p>
    <p>{`Smallifying Lodash, that made me feel cheeky. I ran a quick `}<inlineCode parentName="p">{`git grep`}</inlineCode>{` to see how much of it we use. 32 functions. Out of well over 100 (200? More?) functions in the library.`}</p>
    <p>{`But J Dalton is amazing and publishes `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/browse/keyword/lodash-modularized"
      }}>{`Lodash as a series of modules`}</a>{`. Each function has its own npm module.`}</p>
    <p>{`Armed with my `}<inlineCode parentName="p">{`grep`}</inlineCode>{`, I set to work. The goal:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`import only those Lodash modules that are use
d`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><em parentName="p">{`install`}</em>{` only those that are used`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`avoid manually having to manage Lodash dependencies`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`using `}<inlineCode parentName="p">{`_.somethingLodash`}</inlineCode>{` should work without explicitly importing anything`}</p>
      </li>
    </ul>
    <p>{`Here's what I came up with:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// webpack.config.js

const LodashModules = (() => {
  const path = require("path"),
    execSync = require("child_process").execSync;

  const files = path.resolve(__dirname, "./app/assets/javascripts"),
    code = execSync(\`grep -r "_\\\\\\." \${files} --exclude=mathquill.js\`, {
      encoding: "utf-8",
    });

  const matches = new Set(code.match(/_\\.([a-z]+)/gi));

  return Array.from(matches.values()).map((m) => {
    let module = m.replace("_.", "lodash.").toLowerCase(),
      vars = [m];

    if (module === "lodash.extend") {
      module = "lodash.assign";
      vars = vars.concat("_.assign");
    }
    if (module === "lodash.each") {
      module = "lodash.foreach";
      vars = vars.concat("_.forEach");
    }

    try {
      require.resolve(module);
    } catch (e) {
      console.log(\`Installing \${module}\`);
      execSync(\`npm install --save \${module}\`);
    }

    return new webpack.ProvidePlugin(_.fromPairs(vars.map((v) => [v, module])));
  });
})();

// ...

plugins: [
  // ..
  new webpack.IgnorePlugin(/^\\.\\/locale$/, /moment$/),
].concat(LodashModules);
`}</code></pre>
    <p>{`It's dirty, cheeky, and beautifully insane. It might be brittle, but I found no signs of brokenness. It's slow on first run and gets fast once all your modules are installed. Subsequent Webpack runs are just as fast as they've always been.`}</p>
    <p>{`And yes, you definitely should test your app thoroughly after using this. Very thoroughly.`}</p>
    <p>{`I think it's similar to J Dalton's `}<a parentName="p" {...{
        "href": "https://github.com/lodash/babel-plugin-lodash"
      }}>{`babel-plugin-lodash`}</a>{` in its core idea. To use only those parts of Lodash that you use.`}</p>
    <p>{`It differs in how this goal is achieved. Dalton's plugin uses proper code analysis to find functions that are used and import them as `}<inlineCode parentName="p">{`lodash/<function>`}</inlineCode>{` from a full install of Lodash.`}</p>
    <p>{`My solution goes for modular installation as well modular usage. If I'm not mistaken, this is easier for Webpack to optimize.`}</p>
    <p>{`And I think Dalton's plugin needs you to explicitly import Lodash when you use it. Mine turns it into a magic super global via `}<inlineCode parentName="p">{`webpack.ProvidePlugin`}</inlineCode>{`.`}</p>
    <p>{`In a nutshell, when you say `}<inlineCode parentName="p">{`_.map(stuff)`}</inlineCode>{`, my solution transforms that into `}<inlineCode parentName="p">{`require('lodash.map')(stuff)`}</inlineCode>{`.`}</p>
    <p>{`Let me explain.`}</p>
    <h3 {...{
      "id": "find-all-occurrences-of-lodash-use"
    }}>{`Find all occurrences of Lodash use`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const LodashModules = (() => {
    const path = require('path'),
          execSync = require('child_process').execSync;

    const files = path.resolve(__dirname, './app/assets/javascripts'),
          code = execSync(\`grep -r "_\\\\\\." \${files} --exclude=mathquill.js\`,
                          {encoding: 'utf-8'});


    const matches = new Set(code.match(/_\\.([a-z]+)/ig));
`}</code></pre>
    <p>{`We run a `}<inlineCode parentName="p">{`grep -r "_\\." <files>`}</inlineCode>{` search as a shell command. This returns all lines in all our files that use Lodash. I'm excluding `}<inlineCode parentName="p">{`mathquill.js`}</inlineCode>{` in my case because it's breaking convention and building its own old skool Lodash internally. Old library :)`}</p>
    <p>{`With `}<inlineCode parentName="p">{`grep`}</inlineCode>{` output in hand, we run a regex that extracts all occurrences of `}<inlineCode parentName="p">{`_.something`}</inlineCode>{`. That means chaining doesn't work. If you're used to composing Lodash methods, you're fine; if you're not, you should get used to it.`}</p>
    <p><inlineCode parentName="p">{`babel-lodash-plugin`}</inlineCode>{` also doesn't understand chaining by the way.`}</p>
    <p>{`All occurrences of `}<inlineCode parentName="p">{`_.something`}</inlineCode>{` are put in a `}<inlineCode parentName="p">{`Set`}</inlineCode>{`. This removes any duplicates (of which there are many) and pares us down to 32 functions in my case.`}</p>
    <h3 {...{
      "id": "make-magic-imports"
    }}>{`Make magic imports`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    return Array.from(matches.values())
                .map(m => {
                    let module = m.replace('_.', 'lodash.').toLowerCase(),
                        vars = [m];

                    if (module === 'lodash.extend') {
                        module = 'lodash.assign';
                        vars = vars.concat('_.assign');
                    }
                    if (module === 'lodash.each') {
                        module = 'lodash.foreach';
                        vars = vars.concat('_.forEach');
                    }

                    try {
                        require.resolve(module);
                    }catch(e) {
                        console.log(\`Installing \${module}\`);
                        execSync(\`npm install --save \${module}\`);
                    }

                    return new webpack.ProvidePlugin(
                        _.fromPairs(vars.map(v => [v, module]))
                    )
                })
})();
`}</code></pre>
    <p>{`When we have those unique Lodash function calls, we walk through them, and for each one we:`}</p>
    <ul>
      <li parentName="ul">{`get a clean module name, `}<inlineCode parentName="li">{`module.replace ...`}</inlineCode></li>
      <li parentName="ul">{`resolve aliases (the list is incomplete, it covers only what our codebase uses)`}</li>
      <li parentName="ul">{`try to import the module`}</li>
      <li parentName="ul">{`if it exists, move on`}</li>
      <li parentName="ul">{`if module doesn't exist, install it and save to package.json`}</li>
      <li parentName="ul">{`create a new `}<inlineCode parentName="li">{`ProvidePlugin`}</inlineCode>{` entry for the module`}</li>
    </ul>
    <p>{`The resulting `}<inlineCode parentName="p">{`ProvidePlugin`}</inlineCode>{` entries are added to the `}<inlineCode parentName="p">{`plugins`}</inlineCode>{` array in our overall config.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/corp-screen-3.png",
        "alt": null
      }}></img></p>
    <p>{`And that's how we removed 55KB of gzipped dependencies. I feel like an evil mastermind.`}</p>
    <p>{`Use this in production carefully.`}</p>
    <hr></hr>
    <p>{`Got questions? Twitter me.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      